/**
 * Shared shell for unauthenticated screens (login, signup, slug landing,
 * password reset, etc.). Wordmark hero on top, card-style form panel
 * centered on a dark canvas with a subtle radial brand-tint mesh.
 *
 * Single source so the brand surface stays consistent and any tweak
 * (mesh angle, card radius, footer link) ripples to every auth screen.
 */
import { type ReactNode } from "react";
import { Wordmark } from "@/components/common/Wordmark";
import { ThemeToggle } from "@/components/theme/ThemeToggle";

interface Props {
  title: string;
  subtitle?: string;
  /** Footer slot rendered below the card (e.g. "Already have an account?"). */
  footer?: ReactNode;
  children: ReactNode;
  /** Wider card variant for multi-field signup + MFA enrollment. */
  wide?: boolean;
}

export function AuthShell({
  title,
  subtitle,
  footer,
  children,
  wide = false,
}: Props): JSX.Element {
  // ``wide`` bumped to ``max-w-lg`` (was ``max-w-md`` pre-31a.2.2) so
  // the MFA enrollment card can fit a 192px QR + manual-entry fallback
  // side-by-side per HoD §3.1. Existing signup callers were never near
  // the bound; the change is visually invisible at signup widths.
  const max = wide ? "max-w-lg" : "max-w-sm";
  return (
    <div
      data-testid="page-ready"
      className="relative flex min-h-screen flex-col bg-surface-canvas text-text-primary"
    >
      {/* Brand mesh — radial gradients, opacity-tuned so light surfaces stay
          subtle and dark surfaces pick up the brand teal/blue glow. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 overflow-hidden opacity-60 dark:opacity-100"
      >
        <div className="absolute -left-40 top-[-10%] h-[480px] w-[480px] rounded-full bg-[radial-gradient(circle,_rgba(0,170,255,0.15),_transparent_70%)] dark:bg-[radial-gradient(circle,_rgba(0,170,255,0.18),_transparent_70%)]" />
        <div className="absolute -right-40 bottom-[-10%] h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle,_rgba(0,229,204,0.12),_transparent_70%)] dark:bg-[radial-gradient(circle,_rgba(0,229,204,0.15),_transparent_70%)]" />
      </div>

      <header className="relative z-10 flex h-16 items-center justify-between px-6">
        <Wordmark height={22} />
        <ThemeToggle />
      </header>

      <main className="relative z-10 flex flex-1 items-center justify-center px-4 py-6">
        <div className={`w-full ${max}`}>
          <div className="rounded-2xl border border-border-default bg-surface-card p-4 shadow-md">
            <h1 className="mb-1 text-2xl font-bold tracking-tight text-text-primary">
              {title}
            </h1>
            {subtitle ? (
              <p className="mb-6 text-sm text-text-secondary dark:text-brand-100/70">
                {subtitle}
              </p>
            ) : (
              <div className="mb-6" />
            )}
            {children}
          </div>
          {footer ? (
            <p className="mt-4 text-center text-sm text-text-secondary dark:text-brand-100/60">
              {footer}
            </p>
          ) : null}
        </div>
      </main>
    </div>
  );
}
